import { Action } from '@reduxjs/toolkit';
import React, { Ref, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

import { getZonedDate, ZonedDate } from '@eika-infrastruktur/date';
import { AttentionMessage } from '@eika/attention-message';
import { IconTypes } from '@eika/icon';

import AvtaleAttentionMessage, { AvtaleInfoMessageType } from '../../avtaler/avtale-info-message';
import { DatoInput, getDateWithCorrectTimezone } from '../../components/dato-input/dato-input';
import { ForsikringButtons, selectedAlternativValue } from '../../components/dekning-buttons/forsikring-buttons';
import RouteToFremtindInfoAndModal from '../../components/route-to-fremtind-info-and-modal/route-to-fremtind-info-and-modal';
import { useKunde } from '../../kundeinfo/kunde-context';
import { updateShouldRouteKundeToFremtind } from '../../meldskade/reducer/skademelding-actions';
import { AppState } from '../../reducers';
import { ForsikringDekningDTO } from '../../types/generated-types';
import { toOptions } from '../forsikring/forsikring';
import { FocusableHtmlElement } from '../hooks';
import { isCurrentSporsmalValid } from '../reducer/current-steg';
import {
    updateForsikringInSkadedatoAndForsikringSvar,
    updateSkadedatoInSkadedatoAndForsikringSvar,
} from '../reducer/sporsmalsflyt-actions';
import { useSporsmalsflytDispatch } from '../reducer/sporsmalsflytDispatchProvider';
import {
    CurrentSkadedatoAndForsikringSporsmal,
    ForsikringListeData,
    isForsikringIngenAlternativerType,
    isForsikringListeType,
} from './skadedato-and-forsikring-types';

import '../radio/radio.scss';
import './skadedato-and-forsikring.scss';

interface ForsikringListeProps {
    currentSporsmal: CurrentSkadedatoAndForsikringSporsmal;
    data: ForsikringListeData;
    focusOnMount: boolean;
    error: boolean;
    errorRef?: Ref<FocusableHtmlElement>;
}

const ForsikringListe = ({ currentSporsmal, data, focusOnMount, error, errorRef }: ForsikringListeProps) => {
    const dispatch = useSporsmalsflytDispatch();

    const onForsikringButtonChange = (value: string) => {
        const selectedAlternativ = data.alternativer.find(
            (alternativ) =>
                selectedAlternativValue(alternativ.forsikringsnummer, alternativ.forsikringversjon) === value,
        );
        dispatch(updateForsikringInSkadedatoAndForsikringSvar(selectedAlternativ));
    };

    return (
        <>
            <ForsikringButtons
                name="Forsikringsnummer"
                title={currentSporsmal.sporsmal.forsikringTittel}
                ingress={currentSporsmal.sporsmal.forsikringIngress}
                options={toOptions(data.alternativer)}
                selectedOption={data.selectedAlternativ}
                onChange={onForsikringButtonChange}
                errorRef={errorRef}
                error={error}
                focusOnMount={focusOnMount}
            />
            {data.selectedAlternativ?.id === ForsikringDekningDTO.FinnerIkkeRiktig && (
                <AvtaleAttentionMessage
                    avtaleInfoMessageType={AvtaleInfoMessageType.FinnerIkkeRiktig}
                    skadedato={currentSporsmal.skadedato}
                    componentToShowIfNoOtherAvtaler={null}
                    className="margin-top--medium"
                />
            )}
        </>
    );
};

interface SkadedatoAndForsikringProps {
    currentSporsmal: CurrentSkadedatoAndForsikringSporsmal;
    showFeilmelding: boolean;
    focusOnMount: boolean;
    errorRef?: Ref<FocusableHtmlElement>;
}

export const SkadedatoAndForsikring = ({
    currentSporsmal,
    showFeilmelding,
    focusOnMount,
    errorRef,
}: SkadedatoAndForsikringProps) => {
    const dispatch = useSporsmalsflytDispatch();
    const dispatchAction: Dispatch<Action> = useDispatch();

    const { shouldRouteKundeToFremtind, routeToFremtindFeatureToggleEnabled } = useSelector(
        (state: AppState) => state.skademelding,
    );
    const [openModal, setOpenModal] = useState<boolean>(true);
    const { kundeinfo } = useKunde();

    const onSkadedatoChange = (dato: ZonedDate) => {
        if (dato !== undefined) {
            const dateWithCorrectTimezone = getDateWithCorrectTimezone(dato);
            dispatch(updateSkadedatoInSkadedatoAndForsikringSvar(dateWithCorrectTimezone, kundeinfo));
            if (routeToFremtindFeatureToggleEnabled) {
                dispatchAction(updateShouldRouteKundeToFremtind(kundeinfo.forsikringer, dato.toString()));
            }
        }
    };

    const isSkadedatoInvalid =
        showFeilmelding && currentSporsmal.skadedato === undefined && !isCurrentSporsmalValid(currentSporsmal);

    return (
        <div className="container__standard-width">
            <DatoInput
                id="dato-sporsmal-skadedato"
                onChange={onSkadedatoChange}
                placeholder="dd.mm.åååå"
                date={currentSporsmal.skadedato ? getZonedDate(new Date(currentSporsmal.skadedato)) : undefined}
                maxDate={getZonedDate(new Date())}
                error={isSkadedatoInvalid ? 'Oppgi en gyldig dato' : undefined}
                focusOnMount={focusOnMount}
                label={currentSporsmal.sporsmal.skadedatoTittel}
                sublabel={currentSporsmal.sporsmal.skadedatoIngress}
                errorRef={errorRef}
            />
            {shouldRouteKundeToFremtind ? (
                <RouteToFremtindInfoAndModal
                    shouldRouteKundeToFremtind={shouldRouteKundeToFremtind}
                    skadedato={currentSporsmal.skadedato}
                    openModal={openModal}
                    setOpenModal={setOpenModal}
                />
            ) : (
                <>
                    {currentSporsmal.skadedato && isForsikringListeType(currentSporsmal.forsikringData) && (
                        <div className="skadedato-and-forsikring-sporsmal__forsikring">
                            <ForsikringListe
                                currentSporsmal={currentSporsmal}
                                focusOnMount={focusOnMount}
                                errorRef={errorRef}
                                error={
                                    showFeilmelding &&
                                    !isCurrentSporsmalValid(currentSporsmal) &&
                                    currentSporsmal.forsikringData.selectedAlternativ === undefined
                                }
                                data={currentSporsmal.forsikringData}
                            />
                        </div>
                    )}
                    {currentSporsmal.skadedato &&
                        currentSporsmal.sporsmal.showFeilmelding &&
                        isForsikringIngenAlternativerType(currentSporsmal.forsikringData) && (
                            <AvtaleAttentionMessage
                                avtaleInfoMessageType={AvtaleInfoMessageType.IngenForsikring}
                                skadedato={currentSporsmal.skadedato}
                                className="margin-top--medium"
                                componentToShowIfNoOtherAvtaler={
                                    <div className="margin-top--medium">
                                        <AttentionMessage
                                            id="skadedato-and-forsikring-sporsmal__attention-message"
                                            type="info"
                                            text="Vi finner ingen gyldig forsikring på den angitte datoen."
                                            icon={IconTypes.INFO_24}
                                        />
                                    </div>
                                }
                            />
                        )}
                </>
            )}
        </div>
    );
};
